import React, { useRef, useEffect } from 'react';
import { Message as MessageComponent } from './Message';
import { Loading } from './ui/Loading';
import type { Message, Model } from '../types';

export interface PanelState {
  messages: Message[];
  isStreaming: boolean;
  error: string | null;
  duration: number | null;
}

export interface ComparisonPanelProps {
  model: Model | null;
  panel: PanelState;
}

const PROVIDER_COLORS: Record<string, string> = {
  openai: 'border-green-400 dark:border-green-600',
  anthropic: 'border-orange-400 dark:border-orange-600',
  google: 'border-blue-400 dark:border-blue-600',
  mistral: 'border-purple-400 dark:border-purple-600',
  deepseek: 'border-cyan-400 dark:border-cyan-600',
  opensource: 'border-gray-400 dark:border-gray-600',
};

const PROVIDER_BG: Record<string, string> = {
  openai: 'bg-green-50 dark:bg-green-900/20',
  anthropic: 'bg-orange-50 dark:bg-orange-900/20',
  google: 'bg-blue-50 dark:bg-blue-900/20',
  mistral: 'bg-purple-50 dark:bg-purple-900/20',
  deepseek: 'bg-cyan-50 dark:bg-cyan-900/20',
  opensource: 'bg-gray-50 dark:bg-gray-800',
};

export const ComparisonPanel: React.FC<ComparisonPanelProps> = ({
  model,
  panel,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [panel.messages]);

  const providerKey = model?.provider?.toLowerCase() || '';
  const borderColor = PROVIDER_COLORS[providerKey] || 'border-gray-300 dark:border-gray-600';
  const headerBg = PROVIDER_BG[providerKey] || 'bg-gray-50 dark:bg-gray-800';

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      {/* Panel header */}
      <div
        className={`px-4 py-2.5 border-b-2 ${borderColor} ${headerBg} flex items-center justify-between flex-shrink-0`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {model?.name || 'Select a model'}
          </span>
          {model && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0">
              {model.provider}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {panel.isStreaming && <Loading size="sm" />}
          {!panel.isStreaming && panel.messages.some((m) => m.role === 'assistant') && (
            <div className="flex items-center gap-2">
              {panel.duration !== null && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {(panel.duration / 1000).toFixed(1)}s
                </span>
              )}
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Done
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {panel.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full p-6">
            <p className="text-gray-400 dark:text-gray-500 text-sm text-center">
              Responses will appear here
            </p>
          </div>
        ) : (
          <div className="px-4 py-4 space-y-1">
            {panel.messages.map((message) => (
              <MessageComponent
                key={message.id}
                message={message}
                modelName={message.role === 'assistant' && model ? model.name : undefined}
                provider={message.role === 'assistant' && model ? model.provider : undefined}
              />
            ))}

            {panel.isStreaming &&
              panel.messages[panel.messages.length - 1]?.role !== 'assistant' && (
                <div className="flex justify-start py-2">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 shadow-sm">
                    <Loading size="sm" />
                  </div>
                </div>
              )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Error display */}
      {panel.error && (
        <div className="mx-3 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded-lg text-xs">
          {panel.error}
        </div>
      )}
    </div>
  );
};
